import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';

/** The bell shows the latest notifications; older ones are not lost, just not listed. */
export const NOTIFICATION_LIMIT = 30;

export interface NotificationItem {
  id: string;
  kind: string;
  title: string;
  body: string;
  merchantId: string | null;
  createdAt: string;
  readAt: string | null;
}

export interface NotificationList {
  items: NotificationItem[];
  unread: number;
}

/** The signed-in user's own notifications (RLS returns nobody else's), and how many are unread. */
export async function fetchNotifications(workspaceId: string): Promise<NotificationList> {
  const [list, unread] = await Promise.all([
    supabase()
      .from('notifications')
      .select('id, kind, title, body, merchant_id, created_at, read_at')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(NOTIFICATION_LIMIT),
    supabase()
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .is('read_at', null),
  ]);
  if (list.error) throw new Error(`Couldn't load notifications: ${list.error.message}`);
  if (unread.error) throw new Error(`Couldn't count notifications: ${unread.error.message}`);
  return {
    items: list.data.map((n) => ({
      id: n.id,
      kind: n.kind,
      title: n.title,
      body: n.body,
      merchantId: n.merchant_id,
      createdAt: n.created_at,
      readAt: n.read_at,
    })),
    unread: unread.count ?? 0,
  };
}

export function useNotificationsQuery(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.notifications(workspaceId),
    queryFn: () => fetchNotifications(workspaceId),
  });
}

/** Marks the given notifications read, or all of them (null). */
export function useMarkNotificationsRead(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[] | null) => {
      const { error } = await supabase().rpc(
        'mark_notifications_read',
        ids === null ? {} : { p_ids: ids },
      );
      if (error) throw new Error(`Couldn't mark notifications read: ${error.message}`);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications(workspaceId) }),
  });
}
